<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
    <div class="d-flex align-items-start justify-content-between flex-wrap gap-3">
      <div>
        <h3 class="mb-1">Rabatter</h3>
        <p class="mb-0" style="max-width:640px; color:var(--bo-muted)">
          Gi rabatt i prosent på utvalgte produkter, eller bare enkelte varianter av dem, i en bestemt periode.
        </p>
      </div>
      <button type="button" class="bo_btn text-nowrap bo_discount-create" @click="goToCreatePage">
        + Opprett rabatt
      </button>
    </div>

    <!-- Search only earns its place once the list is long enough to scan for something. -->
    <div v-if="discounts.length > 5" class="mt-4" style="max-width:340px">
      <input class="form-control" type="text" v-model="search" placeholder="Søk etter rabatt…" />
    </div>

    <div v-if="!loading && discounts.length === 0" class="bo_discount-empty mt-4">
      <div style="font-weight:bold">Ingen rabatter ennå</div>
      <div class="mt-1" style="color:var(--bo-muted)">
        En rabatt gir prosentvis avslag på utvalgte produkter i en bestemt periode — for eksempel et
        åpningstilbud.
      </div>
      <button type="button" class="bo_btn mt-3" @click="goToCreatePage"> + Opprett rabatt </button>
    </div>

    <div v-else-if="!loading && filtered.length === 0" class="mt-4" style="color:var(--bo-muted)">
      Ingen rabatter samsvarer med «{{ search }}».
    </div>

    <div v-else-if="!loading" class="table-responsive-sm mt-3">
      <table class="table align-middle bo_discount-table">
        <thead>
          <tr>
            <th style="width:1%">Status</th>
            <th>Navn</th>
            <th style="width:1%" class="text-end">Rabatt</th>
            <th style="width:1%" class="text-nowrap">Periode</th>
            <th>Gjelder for</th>
            <th style="width:1%"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="discount in filtered" :key="discount.id"
              class="bo_discount-row" :class="{ 'bo_discount-row--inactive': !isLive(discount) }"
              @click="toggle(discount.id)">
            <td>
              <span class="bo_discount-status" :class="'bo_discount-status--' + status(discount).kind">
                {{ status(discount).label }}
              </span>
            </td>
            <td>
              <div style="font-weight:bold">{{ discount.name }}</div>
              <div v-if="discount.description" class="bo_discount-description">{{ discount.description }}</div>
            </td>
            <td class="text-end"><span class="bo_discount-percent">−{{ discount.percent }}%</span></td>
            <td class="text-nowrap">
              <div>{{ getDateText(discount.fromDate) }} – {{ getDateText(discount.toDate) }}</div>
              <div v-if="periodHint(discount)" class="bo_discount-description">{{ periodHint(discount) }}</div>
            </td>
            <td>
              <!-- Counts first, detail on demand: ten product names in a cell is a wall of text that
                   pushes every other column out of shape, which is what this list used to do. The whole
                   row toggles this, so reading a discount never risks navigating away from the list. -->
              <div class="bo_discount-scope">
                <span class="bo_discount-chip">{{ itemNames(discount).length }} produkter</span>
                <span v-if="variantCount(discount) > 0" class="bo_discount-chip">{{ variantCount(discount) }} {{ variantCount(discount) === 1 ? 'variant' : 'varianter' }}</span>
                <span class="bo_discount-chip">{{ channelNames(discount).length }} kanaler</span>
                <span v-if="hasMultiLocations()" class="bo_discount-chip">
                  {{ locationNames(discount).length }} avdelinger
                </span>
                <a class="bo_discount-toggle" @click.stop="toggle(discount.id)">
                  {{ expanded === discount.id ? 'Skjul' : 'Vis' }}
                </a>
              </div>
              <div v-if="expanded === discount.id" class="bo_discount-detail" @click.stop>
                <div><span class="bo_discount-detail-label">Produkter:</span>
                  {{ itemNames(discount).join(', ') || '—' }}</div>
                <div class="mt-1"><span class="bo_discount-detail-label">Kanaler:</span>
                  {{ channelNames(discount).join(', ') || '—' }}</div>
                <div v-if="hasMultiLocations()" class="mt-1">
                  <span class="bo_discount-detail-label">Avdelinger:</span>
                  {{ locationNames(discount).join(', ') || '—' }}</div>
              </div>
            </td>
            <td class="text-end text-nowrap">
              <div class="bo_discount-actions">
                <button class="btn btn-sm btn-outline-secondary"
                        @click.stop="goToEditDiscountPage(discount.id)">Rediger</button>
                <button class="btn btn-sm btn-outline-danger"
                        @click.stop="removeDiscount(discount)">Slett</button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  <ConfirmDialogue ref="confirmDialogue"></ConfirmDialogue>
</template>

<script>
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import LoyaltyService from "@/services/loyalty/LoyaltyService";
  import ConfirmDialogue from "@/components/other/ConfirmDialogue";
  import moment from "moment";
  import MerchantUtil from "@/util/MerchantUtil";
  import LocationUtil from "@/util/LocationUtil";

  const CHANNELS = [
    { id: 'WEB_TAKEAWAY', name: 'Takeaway på Web(nettsiden)' },
    { id: 'WEB_DINE_IN', name: 'QR-bestilling ved bord' },
    { id: 'KIOSK', name: 'Selvbetjeningskiosk' },
    { id: 'POS', name: 'Kasse' }
  ];

  export default {
    components: {LoadingSpinner, ConfirmDialogue},
    data() {
      return {
        loading: true,
        discounts: [],
        items: [],
        search: "",
        expanded: null
      };
    },
    computed: {
      useFunctionality(){
        return MerchantUtil.useFunctionality();
      },
      filtered(){
        const query = this.search.trim().toLowerCase();
        if(!query){
          return this.discounts;
        }
        return this.discounts.filter(discount =>
            (discount.name || '').toLowerCase().includes(query)
            || (discount.description || '').toLowerCase().includes(query));
      }
    },
    mounted() {
      this.getDiscounts();
    },
    methods: {
      hasMultiLocations(){
        return LocationUtil.hasMultiLocations();
      },
      toggle(discountId){
        this.expanded = this.expanded === discountId ? null : discountId;
      },
      /**
       * Whether a discount is doing anything right now — the one thing the old list could not tell you
       * without reading two dates and doing the arithmetic yourself.
       */
      status(discount){
        if(discount.active === false){
          return { kind: 'off', label: 'Deaktivert' };
        }
        const today = moment().format('YYYY-MM-DD');
        if(discount.fromDate && today < discount.fromDate){
          return { kind: 'soon', label: 'Kommer' };
        }
        if(discount.toDate && today > discount.toDate){
          return { kind: 'past', label: 'Utløpt' };
        }
        return { kind: 'live', label: 'Aktiv' };
      },
      isLive(discount){
        return this.status(discount).kind === 'live';
      },
      /** A nudge for the cases worth noticing: about to start, or about to end. */
      periodHint(discount){
        const state = this.status(discount);
        const today = moment().startOf('day');
        if(state.kind === 'soon'){
          const days = moment(discount.fromDate).startOf('day').diff(today, 'days');
          return days <= 0 ? 'starter i dag' : 'starter om ' + days + (days === 1 ? ' dag' : ' dager');
        }
        if(state.kind === 'live' && discount.toDate){
          const days = moment(discount.toDate).startOf('day').diff(today, 'days');
          if(days <= 14){
            return days <= 0 ? 'siste dag' : 'utløper om ' + days + (days === 1 ? ' dag' : ' dager');
          }
        }
        return '';
      },
      goToCreatePage(){
        this.$router.push({ path: 'discountCreate'});
      },
      goToEditDiscountPage(discountId){
        this.$router.push({ path: 'discountEdit', query: { discountId: discountId } });
      },
      getDiscounts(){
        LoyaltyService.getDiscounts().then(
            (response) => {
              this.discounts = response.data.discounts || [];
              this.items = response.data.items || [];
              this.loading = false;
            },
            (error) => {
              console.log(error);
              this.loading = false;
            }
        );
      },
      /**
       * One entry per product the discount covers: the whole product by name, or — when it is only some of
       * its variants — the name followed by those variants, e.g. "12 Margherita (Stor, Familie)".
       *
       * A discount can reference a product or variant that has since been deleted — find() then returns
       * undefined, and reading .menuNr off it took the whole page down. Unknown ids are skipped instead.
       */
      itemNames(discount){
        const label = item => item.menuNr ? item.menuNr + ' ' + item.name : item.name;
        const names = (discount.itemIds || [])
            .map(id => this.items.find(x => x.id === id))
            .filter(Boolean)
            .map(label);
        const variantsByItem = new Map();
        this.selectedVariants(discount).forEach(({ item, variant }) => {
          if(!variantsByItem.has(item)){
            variantsByItem.set(item, []);
          }
          variantsByItem.get(item).push(variant.name);
        });
        variantsByItem.forEach((variantNames, item) => names.push(label(item) + ' (' + variantNames.join(', ') + ')'));
        return names;
      },
      variantCount(discount){
        return this.selectedVariants(discount).length;
      },
      /** The single variants a discount names, each with its product; variants that no longer exist are left out. */
      selectedVariants(discount){
        const ids = discount.subselectionIds || [];
        const found = [];
        ids.forEach(id => {
          for (const item of this.items) {
            const variant = (item.subselections || []).find(subselection => subselection && subselection.id === id);
            if(variant){
              found.push({ item, variant });
              return;
            }
          }
        });
        return found;
      },
      channelNames(discount){
        const ids = discount.validForChannelIds || [];
        return ids.map(id => CHANNELS.find(c => c.id === id))
                  .filter(Boolean)          //An unknown channel id used to crash on .name
                  .map(channel => channel.name);
      },
      locationNames(discount){
        const ids = discount.validForLocationIds || [];
        return ids.map(id => LocationUtil.getLocation(id))
                  .filter(Boolean)
                  .map(location => location.name);
      },
      getDateText(date){
        return date ? moment(date).format('DD.MM.YYYY') : '';
      },
      async removeDiscount(discount){
        const ok = await this.$refs.confirmDialogue.show({
          title: 'Slett rabatt ' + discount.name,
          message: 'Vil du slette rabatt: ' + discount.name + '?',
          okButton: 'Slett',
          cancelButton: 'Avbryt'
        })
        if(ok) {
          LoyaltyService.deleteDiscount(discount).then(
              (response) => {
                if(response.data === 'SUCCEEDED'){
                  this.$router.go(); //Reload current page
                }else {
                  console.log("Deleting discount failed");
                }
              },
              (error) => {
                console.log(error);
              }
          );
        }
      }
    }
  };
</script>

<style scoped>
.bo_discount-table th {
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bo-muted);
  border-bottom: 2px solid #e5e5e5;
}

/* The whole row expands its detail — a harmless, reversible action. Editing stays on the explicit
   Rediger button, so a stray click cannot navigate away from the list. */
.bo_discount-row {
  cursor: pointer;
}

.bo_discount-row:hover {
  background-color: var(--bo-surface-2);
}

/* An expired or disabled discount is still worth listing, but should not compete with a live one. */
.bo_discount-row--inactive {
  color: var(--bo-muted-2);
}

.bo_discount-status {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: bold;
  white-space: nowrap;
}

.bo_discount-status--live { background-color: var(--bo-ok-bg); color: #1a7f37; }
.bo_discount-status--soon { background-color: var(--bo-brand-tint); color: #8A6800; }
.bo_discount-status--past { background-color: var(--bo-surface-2); color: var(--bo-muted); }
.bo_discount-status--off  { background-color: var(--bo-bad-bg); color: #8a3b3b; }

.bo_discount-percent {
  font-weight: bold;
  font-size: 1.05rem;
  white-space: nowrap;
}

.bo_discount-description {
  color: var(--bo-muted);
  font-size: 0.85rem;
}

.bo_discount-scope {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
}

.bo_discount-chip {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  background-color: var(--bo-surface-2);
  border: 1px solid #e2e2e2;
  color: var(--bo-ink-2);
  font-size: 12px;
  white-space: nowrap;
}

.bo_discount-toggle {
  color: var(--bo-muted);
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
}

.bo_discount-detail {
  margin-top: 0.5rem;
  padding: 0.6rem 0.75rem;
  background-color: var(--bo-surface-2);
  border-left: 3px solid #e2e2e2;
  font-size: 0.85rem;
  cursor: default;
}

.bo_discount-detail-label {
  color: var(--bo-muted);
}

.bo_discount-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.bo_discount-empty {
  padding: 2rem;
  border: 1px dashed var(--bo-line);
  border-radius: 8px;
  background-color: var(--bo-surface-2);
  max-width: 640px;
}

/* ---------------------------------------------------------------------------------------------
   Mobile: each row becomes a card rather than a six-column table behind a horizontal scrollbar.
   A grid rather than :before labels (the pattern used elsewhere in the app), because these cells
   are self-describing — a status badge, a name, a percentage — and a "Status:" prefix in front of
   "Aktiv" would be noise. The grid also lets the badge and the percentage share the top line,
   which stacking in DOM order could not do.
   --------------------------------------------------------------------------------------------- */
@media (max-width: 767.98px) {
  .bo_discount-table thead {
    display: none;
  }

  .bo_discount-table,
  .bo_discount-table tbody {
    display: block;
    width: 100%;
  }

  .bo_discount-table tr {
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      "status  .       percent"
      "name    name    name"
      "period  period  period"
      "scope   scope   scope"
      "actions actions actions";
    gap: 0.35rem 0.5rem;
    padding: 0.9rem 1rem;
    margin-bottom: 0.75rem;
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    background-color: var(--bo-surface);
  }

  .bo_discount-table td {
    display: block;
    border: none;
    padding: 0;
  }

  .bo_discount-table td:nth-child(1) { grid-area: status; }
  .bo_discount-table td:nth-child(2) { grid-area: name; }
  .bo_discount-table td:nth-child(3) { grid-area: percent; }
  .bo_discount-table td:nth-child(4) { grid-area: period; }
  .bo_discount-table td:nth-child(5) { grid-area: scope; }
  .bo_discount-table td:nth-child(6) { grid-area: actions; }

  /* The desktop table right-aligns these; in a card they read better from the left. */
  .bo_discount-table td.text-end {
    text-align: left !important;
  }

  .bo_discount-table td:nth-child(3) {
    text-align: right !important;
  }

  /* An inactive card dims as a whole, so the border carries the distinction the row shading did. */
  .bo_discount-row--inactive {
    background-color: var(--bo-surface-2) !important;
    border-color: #ececec;
  }

  .bo_discount-row:hover {
    background-color: var(--bo-surface);
  }

  /* Full-width, comfortably tappable — 44px is the usual floor for a touch target. */
  .bo_discount-actions {
    margin-top: 0.35rem;
    gap: 0.5rem;
  }

  .bo_discount-actions .btn {
    flex: 1;
    min-height: 44px;
  }

  .bo_discount-create {
    width: 100%;
  }

  .bo_discount-detail {
    margin-left: 0;
  }
}
</style>
